import React, { useState, useEffect } from 'react';
import { Card, CardBody, Col, Container, Row, Table, CardHeader, Button } from 'reactstrap';
import { Link, useParams } from 'react-router-dom';
import MetaTags from "react-meta-tags";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import ApiClient from '../BaseUrl/ApiClient';

function HHDetail() {
    const { dealId } = useParams();
    const [deal, setDeal] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchDealDetails = async () => {
        setLoading(true);
        setError(null);
        try {
            const client = ApiClient();
            const response = await client.get(`business/deal/happyHours/${dealId}`);
            const fetchedDeal = response.data.responseData.deal;
            setDeal(fetchedDeal);
        } catch (err) {
            setError('Failed to fetch happy hour details. Please try again.');
            toast.error('Failed to fetch happy hour details.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (dealId) {
            fetchDealDetails();
        }
    }, [dealId]);

    const formatTime = (timeString) => {
        if (!timeString) return 'N/A';
        
        try {
            // Handle ISO time strings (like "1970-01-01T01:32:00.000Z")
            if (typeof timeString === 'string' && timeString.includes('T')) {
                const date = new Date(timeString);
                if (isNaN(date.getTime())) return 'Invalid time';
                
                // Extract hours and minutes in UTC (since it's a Zulu time)
                const hours = date.getUTCHours();
                const minutes = date.getUTCMinutes();
                
                // Convert to 12-hour format
                const ampm = hours >= 12 ? 'PM' : 'AM';
                const displayHours = hours % 12 || 12; // Convert 0 to 12 for 12 AM
                
                // Format with leading zeros for minutes
                const formattedMinutes = minutes.toString().padStart(2, '0');
                
                return `${displayHours}:${formattedMinutes} ${ampm}`;
            }
            
            // Handle simple HH:mm format strings
            if (/^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/.test(timeString)) {
                const [hours, minutes] = timeString.split(':');
                const hourNum = parseInt(hours, 10);
                const ampm = hourNum >= 12 ? 'PM' : 'AM';
                const displayHour = hourNum % 12 || 12;
                return `${displayHour}:${minutes} ${ampm}`;
            }
            
            return 'Invalid time format';
        } catch (e) {
            console.error('Error formatting time:', e);
            return 'Invalid time';
        }
    };

    if (loading) {
        return (
            <div className="page-content">
                <Container fluid className="mt-5">
                    <div className="text-center">
                        <lord-icon
                            src="https://cdn.lordicon.com/msoeawqm.json"
                            trigger="loop"
                            colors="secondary:#121331,secondary:#08a88a"
                            style={{ width: '75px', height: '75px' }}
                        ></lord-icon>
                        <h5 className="mt-2">Loading Happy Hour Details...</h5>
                    </div>
                </Container>
            </div>
        );
    }

    if (error || !deal) {
        return (
            <div className="page-content">
                <Container fluid className="mt-5">
                    <div className="text-center">
                        <lord-icon
                            src="https://cdn.lordicon.com/msoeawqm.json"
                            trigger="loop"
                            colors="secondary:#121331,secondary:#08a88a"
                            style={{ width: '75px', height: '75px' }}
                        ></lord-icon>
                        <h5 className="mt-2">Sorry! No Happy Hour Found</h5>
                        <p className="text-muted mb-0">{error || 'No happy hour data available.'}</p>
                    </div>
                </Container>
            </div>
        );
    }

    return (
        <React.Fragment>
            <div className="page-content">
                <Container fluid className="mt-5">
                    <MetaTags>
                        <title>Happy Hour Details</title>
                    </MetaTags>
                    <ToastContainer />

                    <div className="mb-3 ms-4">
                        <Link to="/happy-hour-deal-list">
                            <Button color="primary" className="btn ">
                                <i className="mdi mdi-arrow-left"></i>
                            </Button>
                        </Link>
                    </div>

                    <div className="deal-container">
                        <Row>
                            <Col lg="4" md="6">  
                                <div className="deal-image-container mb-4">
                                    {deal.dealPhoto && deal.dealPhoto.length > 0 ? (
                                        <div>
                                            <h5 className="mb-3">Happy Hour Photos</h5>
                                            <div className="d-flex flex-wrap gap-3">
                                                {deal.dealPhoto.map((photo, index) => (
                                                    <div key={index} className="position-relative">
                                                        <img
                                                            src={photo.url}
                                                            alt={`Deal Photo ${index + 1}`}
                                                            className="img-thumbnail"
                                                            style={{
                                                                width: '150px',
                                                                height: '150px',
                                                                objectFit: 'contain'
                                                            }}
                                                        />
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="text-center p-4 border rounded">
                                            <i className="mdi mdi-image-off display-4 text-muted"></i>
                                            <p className="mt-2 mb-0">No photos available</p>
                                        </div>
                                    )}
                                </div>
                            </Col>
                            <Col lg="8" md="6">   
                                <div className="deal-details">
                                    <div className='d-flex deal-category align-items-center '>
                                        <div className={`deal-status ${deal.isActive ? 'active' : 'inactive'}`}>
                                            {deal.isActive ? 'Available' : 'Currently Unavailable'}
                                        </div>
                                    </div>

                                    <div className="deal-category d-flex align-items-center">
                                        <p className='dealsubhead'>Category:</p>
                                        <span className='catName'>{deal.category?.name || 'N/A'}</span>
                                    </div>
                                    
                                    <div className=" deal-category time-range">
                                        <p className='dealsubhead'>Happy Hour Time:</p>
                                        {deal.timeRange ? (
                                            <p className='catName mb-0'>
                                                {formatTime(deal.timeRange.startTime)} - {formatTime(deal.timeRange.endTime)}
                                            </p>
                                        ) : (
                                            <p>No time range specified</p>
                                        )}
                                    </div>
                                    
                                    {deal.days && deal.days.length > 0 && (
                                        <div className="days-section">
                                            <p className='dealsubhead'>Available Days:</p>
                                            <div className="days-pills">
                                                {deal.days.map((day, index) => (
                                                    <span key={index} className="day-pill">{day}</span>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>  
                            </Col>
                        </Row>
                    </div>
                </Container>
            </div>


        </React.Fragment>
    );
}

export default HHDetail;